import { VALIDATION, QUESTION_RESPONSES, ERROR_MESSAGES } from './constants';

/**
 * Survey validation utilities
 * 
 * This module provides validation functions for survey data, form fields,
 * and business rules to ensure data integrity and completeness.
 */

/**
 * Validates required text fields like strengths and opportunities
 * 
 * @param {string} value - The field value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} Validation result with isValid and message
 */
export const validateRequiredTextField = (value, fieldName) => {
  // Check if value exists and is not empty
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return {
      isValid: false,
      message: `${fieldName} is required`,
    };
  }

  // Check minimum length
  if (value.trim().length < VALIDATION.MIN_TEXT_LENGTH) {
    return {
      isValid: false,
      message: `${fieldName} must be at least ${VALIDATION.MIN_TEXT_LENGTH} characters long`,
    };
  }

  // Check maximum length
  if (value.length > VALIDATION.MAX_TEXT_LENGTH) {
    return {
      isValid: false,
      message: `${fieldName} must be no more than ${VALIDATION.MAX_TEXT_LENGTH} characters long`,
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

/**
 * Validates explanation fields that are required when questions fail
 * 
 * @param {string} value - The explanation value
 * @param {boolean} isRequired - Whether this field is required based on question responses
 * @returns {Object} Validation result
 */
export const validateExplanationField = (value, isRequired) => {
  // If not required, any value (including empty) is valid
  if (!isRequired) {
    return {
      isValid: true,
      message: '',
    };
  }

  // If required, apply the same validation as required text fields
  return validateRequiredTextField(value, 'Explanation');
};

/**
 * Validates responsibility selection fields
 * 
 * @param {string} value - The selected responsibility value
 * @param {boolean} isRequired - Whether this field is required
 * @returns {Object} Validation result
 */
export const validateResponsibilityField = (value, isRequired) => {
  if (!isRequired) {
    return {
      isValid: true,
      message: '',
    };
  }

  if (!value || value === '' || value === '0') {
    return {
      isValid: false,
      message: 'Please select who is responsible for addressing this issue',
    };
  }

  // Check if value is one of the allowed options
  const allowedValues = ['Facilities', 'Store'];
  if (!allowedValues.includes(value)) {
    return {
      isValid: false,
      message: 'Please select a valid responsibility option',
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

/**
 * Validates question responses (pass/fail values)
 * 
 * @param {string|number} value - The question response value
 * @returns {Object} Validation result
 */
export const validateQuestionResponse = (value) => {
  const stringValue = String(value);
  
  if (stringValue !== QUESTION_RESPONSES.PASS && stringValue !== QUESTION_RESPONSES.FAIL) {
    return {
      isValid: false,
      message: 'Please select a valid response for this question',
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

/**
 * Determines if follow-up fields (explanation, responsibility, images) are required
 * based on the question responses in a section/area
 * 
 * @param {Object} formValues - Current form values
 * @param {string} sectionIndex - Section index (e.g., "1")
 * @param {string} areaIndex - Area index (e.g., "2")
 * @returns {boolean} True if follow-up fields are required
 */
export const areFollowUpFieldsRequired = (formValues, sectionIndex, areaIndex) => {
  const prefix = `${sectionIndex}-${areaIndex}`;
  
  // Check all question fields in this section/area
  for (const fieldName in formValues) {
    if (fieldName.startsWith(prefix) && !fieldName.includes('-explain') && 
        !fieldName.includes('-responsible') && !fieldName.includes('-files')) {
      
      const value = formValues[fieldName];
      
      // If any question in this area has a failing response, follow-up fields are required
      if (String(value) === QUESTION_RESPONSES.FAIL) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Validates all form data for a complete survey
 * 
 * @param {Object} formValues - All form values
 * @param {Object} surveyInfo - Survey structure information
 * @returns {Object} Validation result with details
 */
export const validateCompleteSurvey = (formValues, surveyInfo) => {
  const errors = {};
  let isValid = true;

  // Validate required summary fields
  VALIDATION.REQUIRED_FIELDS.forEach(fieldName => {
    const validation = validateRequiredTextField(formValues[fieldName], fieldName);
    if (!validation.isValid) {
      errors[fieldName] = validation.message;
      isValid = false;
    }
  });

  // Validate all question responses and their follow-up fields
  if (surveyInfo) {
    Object.keys(surveyInfo).forEach((sectionKey, sectionIndex) => {
      const section = surveyInfo[sectionKey];
      
      // Guard clause: ensure section is an array
      if (!Array.isArray(section)) {
        return;
      }
      
      // Group questions by area
      const areaGroups = {};
      section.forEach(question => {
        if (!areaGroups[question.area]) {
          areaGroups[question.area] = [];
        }
        areaGroups[question.area].push(question);
      });

      // Validate each area
      Object.keys(areaGroups).forEach((areaKey, areaIndex) => {
        const questions = areaGroups[areaKey];
        const sectionNum = sectionIndex + 1;
        const areaNum = areaIndex + 1;
        const prefix = `${sectionNum}-${areaNum}`;

        // Check if follow-up fields are required for this area
        const followUpRequired = areFollowUpFieldsRequired(formValues, String(sectionNum), String(areaNum));

        // Validate individual questions
        questions.forEach(question => {
          const fieldName = `${prefix}-${question.id}`;
          const value = formValues[fieldName];
          
          if (value !== undefined) {
            const validation = validateQuestionResponse(value);
            if (!validation.isValid) {
              errors[fieldName] = validation.message;
              isValid = false;
            }
          }
        });

        // Validate follow-up fields if required
        if (followUpRequired) {
          // Validate explanation
          const explanationField = `${prefix}-explain`;
          const explanationValidation = validateExplanationField(
            formValues[explanationField], 
            true
          );
          if (!explanationValidation.isValid) {
            errors[explanationField] = explanationValidation.message;
            isValid = false;
          }

          // Validate responsibility
          const responsibilityField = `${prefix}-responsible`;
          const responsibilityValidation = validateResponsibilityField(
            formValues[responsibilityField], 
            true
          );
          if (!responsibilityValidation.isValid) {
            errors[responsibilityField] = responsibilityValidation.message;
            isValid = false;
          }

          // Note: Image validation is optional - we don't require images for failed questions
          // but if they exist, they should be valid (handled by the image upload hook)
        }
      });
    });
  }

  return {
    isValid,
    errors,
    errorCount: Object.keys(errors).length,
    errorFields: Object.keys(errors),
  };
};

/**
 * Gets validation rules for React Hook Form
 * 
 * @param {string} fieldType - Type of field ('required-text', 'explanation', 'responsibility', 'question')
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Object} React Hook Form validation rules
 */
export const getValidationRules = (fieldType, isRequired = false) => {
  const baseRules = {};

  switch (fieldType) {
    case 'required-text':
      baseRules.required = {
        value: true,
        message: 'This field is required',
      };
      baseRules.minLength = {
        value: VALIDATION.MIN_TEXT_LENGTH,
        message: `Must be at least ${VALIDATION.MIN_TEXT_LENGTH} characters long`,
      };
      baseRules.maxLength = {
        value: VALIDATION.MAX_TEXT_LENGTH,
        message: `Must be no more than ${VALIDATION.MAX_TEXT_LENGTH} characters long`,
      };
      break;

    case 'explanation':
      if (isRequired) {
        baseRules.required = {
          value: true,
          message: 'Please explain the issue(s) found',
        };
        baseRules.minLength = {
          value: VALIDATION.MIN_TEXT_LENGTH,
          message: `Explanation must be at least ${VALIDATION.MIN_TEXT_LENGTH} characters long`,
        };
      }
      baseRules.maxLength = {
        value: VALIDATION.MAX_TEXT_LENGTH,
        message: `Explanation must be no more than ${VALIDATION.MAX_TEXT_LENGTH} characters long`,
      };
      break;

    case 'responsibility':
      if (isRequired) {
        baseRules.required = {
          value: true,
          message: 'Please select who is responsible',
        };
        baseRules.validate = (value) => {
          if (!value || value === '' || value === '0') {
            return 'Please select who is responsible';
          }
          return true;
        };
      }
      break;

    case 'question':
      baseRules.required = {
        value: true,
        message: 'Please select a response for this question',
      };
      baseRules.validate = (value) => {
        const validation = validateQuestionResponse(value);
        return validation.isValid || validation.message;
      };
      break;

    default:
      break;
  }

  return baseRules;
};

/**
 * Formats validation errors for display to users
 * 
 * @param {Object} errors - Validation errors object
 * @returns {Array} Array of formatted error messages
 */
export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([fieldName, message]) => ({
    field: fieldName,
    message: message,
    displayName: formatFieldName(fieldName),
  }));
};

/**
 * Converts field names to user-friendly display names
 * 
 * @param {string} fieldName - Technical field name
 * @returns {string} User-friendly field name
 */
export const formatFieldName = (fieldName) => {
  // Handle special cases
  if (fieldName === 'strengths') return 'Strengths';
  if (fieldName === 'opportunities') return 'Opportunities';
  
  // Handle section-area-field format
  if (fieldName.includes('-')) {
    const parts = fieldName.split('-');
    if (parts.length >= 3) {
      const section = parts[0];
      const area = parts[1];
      const field = parts[2];
      
      if (field === 'explain') return `Section ${section}.${area} - Explanation`;
      if (field === 'responsible') return `Section ${section}.${area} - Responsibility`;
      if (field === 'files') return `Section ${section}.${area} - Images`;
      
      return `Section ${section}.${area} - Question ${field}`;
    }
  }
  
  // Default formatting
  return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
};